<template>
  <svg id="svg" width="340" height="165" @mouseup="endDrag" @mouseleave="endDrag" @mousemove="drag($event)">
    <!-- 装饰性背景网格 -->
    <g class="matrix_grid">
      <line v-for="i in 4" :key="'vx' + i" :x1="(i - 1) * pointX + offsetX + 15" y1="10" :x2="(i - 1) * pointX + offsetX + 15" y2="170" stroke="rgba(0,0,0,0.06)" stroke-width="1" stroke-dasharray="2,2" />
      <line v-for="j in 4" :key="'hy' + j" x1="15" :y1="(j - 1) * pointY + 6 + 15" x2="325" :y2="(j - 1) * pointY + 6 + 15" stroke="rgba(0,0,0,0.06)" stroke-width="1" stroke-dasharray="2,2" />
    </g>

    <g v-for="(item, row) in props.data" :key="row">
      <g v-for="(point, col) in item.KeyFunction">
        <!-- 激活状态的滑块 -->
        <g :id="'group' + row + '_' + col" @mousedown="startDrag($event, row, col)" :style="{ display: pointDisplay(row, col, false) }" class="point_group active">
          <rect :width="rectPointWidth(row, col, point)" height="30" :x="col * pointX + offsetX" :y="row * pointY + 6" rx="15" ry="15" fill="var(--brand-color-pure)" class="active_rect" />
          <circle :id="'white' + row + '_' + col" :cx="col * pointX + offsetX + 15" :cy="row * pointY + 6 + 15" r="4" fill="#fff" />
        </g>
        <!-- 未激活：可点击 -->
        <g v-if="props.data[row].KeyFunction[col] === 0 && !isBlockedForSingleClick(row, col)" :id="'default_group' + row + '_' + col" @mousedown="startDrag($event, row, col)" class="point_group default">
          <circle :cx="col * pointX + offsetX + 15" :cy="row * pointY + 6 + 15" r="15" fill="rgba(0,0,0,0.04)" class="default_circle" />
          <line :x1="col * pointX + offsetX + 10" :y1="row * pointY + 6 + 15" :x2="col * pointX + offsetX + 20" :y2="row * pointY + 6 + 15" stroke="var(--color-text-hint)" stroke-width="2" stroke-linecap="round" />
          <line :x1="col * pointX + offsetX + 15" :y1="row * pointY + 6 + 10" :x2="col * pointX + offsetX + 15" :y2="row * pointY + 6 + 20" stroke="var(--color-text-hint)" stroke-width="2" stroke-linecap="round" />
        </g>
        <!-- 未激活：被锁定（持续按压链后无空位间隔） -->
        <g v-else-if="props.data[row].KeyFunction[col] === 0" :id="'default_group' + row + '_' + col" @mousedown="startDrag($event, row, col)" class="point_group blocked">
          <title>持续按压期间无法插入单击，可向右拖拽延续按压</title>
          <circle :cx="col * pointX + offsetX + 15" :cy="row * pointY + 6 + 15" r="15" fill="rgba(0,0,0,0.04)" class="blocked_circle" />
          <line :x1="col * pointX + offsetX + 10" :y1="row * pointY + 6 + 15" :x2="col * pointX + offsetX + 20" :y2="row * pointY + 6 + 15" stroke="var(--color-text-hint)" stroke-width="2" stroke-linecap="round" />
        </g>
      </g>
    </g>
  </svg>
</template>

<script lang="ts" setup>
import { SetMagnetKey } from '@libs/types/response'
import { ref, watch } from 'vue'

// 判断某个位置是否被持续按压链封锁（仅在 restrictHoldChain=true 时生效）
// 规则：向左扫描，遇到 0 即断链（允许），遇到 >= 2 禁止，遇到 1 允许
const isBlockedForSingleClick = (row: number, col: number): boolean => {
  if (!props.restrictHoldChain) return false
  for (let i = col - 1; i >= 0; i--) {
    const val = props.data[row].KeyFunction[i]
    if (val === 0) return false // 遇到 0，链断，允许
    if (val >= 2) return true // 键仍处于 hold 状态，禁止
    if (val === 1) return false // 键已释放，允许
  }
  return false
}
const props = defineProps<{ data: SetMagnetKey[]; selectedRow?: number; restrictHoldChain?: boolean; normalizeHold?: boolean }>()
const emits = defineEmits(['changeKeyFunction'])

// normalizeHold 设备不区分 v=3 与 v=2，加载旧数据时自动归一化
watch(
  () => props.data,
  (data) => {
    if (!props.normalizeHold) return
    data.forEach((item, row) => {
      item.KeyFunction.forEach((v, col) => {
        if (v === 3) emits('changeKeyFunction', row, col, 2)
      })
    })
  },
  { immediate: true, deep: true },
)

const draging = ref(false)
const dragingPoint = ref(0)
const isSetStartPoint = ref(false)
const startX = ref(0)
const pointRow = ref(0)
const pointCol = ref(0)
const maxWidth = ref(0)
const isClick = ref(false)

const pointX = 86
const pointY = 42
const offsetX = 26 // Offset to center 4 points in 340px width

const clickPoint = (row: number, col: number) => {
  if (props.data[row].KeyFunction[col] > 1) {
    // Jingtai：持续链内的位置不能变成 1，截断链用 0
    const endValue = isBlockedForSingleClick(row, col) ? 0 : 1
    emits('changeKeyFunction', row, col, endValue)
    for (let i = col + 1; i < props.data[row].KeyFunction.length; i++) {
      if (props.data[row].KeyFunction[i] == 2) {
        emits('changeKeyFunction', row, i, 0)
      }
      if (props.data[row].KeyFunction[i] == 3) {
        emits('changeKeyFunction', row, i, 2)
      }
    }
    return
  }
  if (props.data[row].KeyFunction[col] == 1) {
    emits('changeKeyFunction', row, col, 0)
  } else {
    if (isBlockedForSingleClick(row, col)) return
    emits('changeKeyFunction', row, col, 1)
  }
}

const startDrag = (event: MouseEvent, row: number, col: number) => {
  if (event.button === 2) {
    return
  }

  // 最后一个点位无处延伸，禁止拖拽（仍可单击切换）
  const isLastCol = col === props.data[row].KeyFunction.length - 1
  if (isLastCol) {
    // 仅处理单击逻辑，不进入拖拽流程
    clickPoint(row, col)
    return
  }

  isClick.value = true
  draging.value = true
  dragingPoint.value = props.data[row].KeyFunction[col]
  startX.value = event.clientX
  pointRow.value = row
  pointCol.value = col

  const group = document.getElementById('group' + row + '_' + col)
  const svg = document.getElementById('svg')

  if (svg && group) {
    svg.appendChild(group as HTMLElement)
  }

  let rowLength = props.data[pointRow.value].KeyFunction.length - 1
  for (let i = col; i < rowLength; i++) {
    if (col == i) {
      continue
    }
    if (props.data[pointRow.value].KeyFunction[i] > 0) {
      rowLength = i
      break
    }
  }
  // normalizeHold 设备：相邻持续链会被固件合并为一次，强制留出 1 格间距
  const hasAdjacentHold = props.normalizeHold && rowLength < props.data[pointRow.value].KeyFunction.length - 1 && props.data[pointRow.value].KeyFunction[rowLength] >= 2
  maxWidth.value = hasAdjacentHold ? Math.max(0, (rowLength - col - 1) * pointX) : (rowLength - col) * pointX
}
const drag = (event: MouseEvent) => {
  if (draging.value && event.clientX - startX.value > 10) {
    if (dragingPoint.value < 2) {
      isClick.value = false

      if (!isSetStartPoint.value) {
        setStartPoint()
      }

      let dragW = event.clientX - startX.value > 0 ? event.clientX - startX.value : 0
      let w = dragW < 30 ? 30 : dragW
      w = w > maxWidth.value ? maxWidth.value : w
      setRectWidth(pointRow.value, pointCol.value, w)
    }
  }
}

const setStartPoint = () => {
  isSetStartPoint.value = true
  // normalizeHold 设备：若紧邻右侧已有持续按压，跳过创建 hold（固件会合并相邻链，点击行为仍可正常触发）
  if (props.normalizeHold) {
    const nextCol = pointCol.value + 1
    const nextVal = nextCol < props.data[pointRow.value].KeyFunction.length ? props.data[pointRow.value].KeyFunction[nextCol] : 0
    if (nextVal >= 2) return
  }
  if (!props.normalizeHold && pointCol.value > 0 && props.data[pointRow.value].KeyFunction[pointCol.value - 1] > 1) {
    emits('changeKeyFunction', pointRow.value, pointCol.value, 3)
  } else {
    emits('changeKeyFunction', pointRow.value, pointCol.value, 2)
  }
}

const endDrag = () => {
  if (draging.value) {
    if (isClick.value) {
      clickPoint(pointRow.value, pointCol.value)
      isClick.value = false
    } else if (dragingPoint.value < 2) {
      const group = document.getElementById('group' + pointRow.value + '_' + pointCol.value)
      const rect = group?.querySelector('rect')
      if (rect) {
        let w = rect.getAttribute('width')
        if (w) {
          let sw = 0
          let point = 0
          let pointWidth = parseInt(w)
          if ((pointWidth > pointX && pointX - (pointWidth % pointX) > 28) || (pointWidth < pointX && pointX - pointWidth > 28)) {
            point = Math.floor(parseInt(w) / pointX)
          } else {
            point = Math.ceil(parseInt(w) / pointX)
          }
          if (!props.normalizeHold && props.data[pointRow.value].KeyFunction[pointCol.value] > 1 && props.data[pointRow.value].KeyFunction[pointCol.value + point] == 2) {
            emits('changeKeyFunction', pointRow.value, pointCol.value + point, 3)
          }

          // normalizeHold 设备：裁剪 point，确保新 hold 尾部不与已有 hold 相邻
          if (props.normalizeHold) {
            while (point > 0) {
              const nextCol = pointCol.value + point
              if (nextCol < props.data[pointRow.value].KeyFunction.length && props.data[pointRow.value].KeyFunction[nextCol] >= 2) {
                point--
              } else {
                break
              }
            }
          }

          for (let i = pointCol.value; i < point; i++) {
            emits('changeKeyFunction', pointRow.value, pointCol.value + i, 2)
          }

          sw = point > 0 ? point * pointX : 30
          rect.setAttribute('width', sw.toString())

          // 拖拽完成后，清除因新的持续链而变违规的 1
          const rowLen = props.data[pointRow.value].KeyFunction.length
          for (let c = 0; c < rowLen; c++) {
            if (props.data[pointRow.value].KeyFunction[c] === 1 && isBlockedForSingleClick(pointRow.value, c)) {
              emits('changeKeyFunction', pointRow.value, c, 0)
            }
          }
        }
      }
    }
    draging.value = false
    isSetStartPoint.value = false
    dragingPoint.value = 0
    pointRow.value = 0
    pointCol.value = 0
  }
}

const setRectWidth = (row: number, col: number, w: number) => {
  const group = document.getElementById('group' + row + '_' + col)
  const rect = group?.querySelector('rect')
  if (rect) {
    rect.setAttribute('width', w.toString())
  }
}

const pointDisplay = (row: number, col: number, isDefault: boolean) => {
  let point = props.data[row].KeyFunction[col]
  let display = isDefault ? (point == 0 ? 'block' : 'none') : point > 0 ? 'block' : 'none'
  if (isDefault) {
    display = point == 0 ? 'block' : 'none'
  } else {
    display = point > 0 ? 'block' : 'none'
    if (col > 0 && point == 2) {
      let previousPoint = props.data[row].KeyFunction[col - 1]
      if (previousPoint > 1) {
        display = 'none'
      }
    }
  }
  return display
}

const rectPointWidth = (row: number, col: number, point: number) => {
  let w = 30
  if (point > 1) {
    let pointNum = 0

    let rowLength = props.data[row].KeyFunction.length - 1
    for (let i = col; i < rowLength; i++) {
      if (col == i) {
        continue
      }
      if (props.data[row].KeyFunction[i] < 2) {
        rowLength = i
        break
      }
    }

    for (let i = 0; i < rowLength; i++) {
      if (props.data[row].KeyFunction[col + i] == 1 || (i > 0 && props.data[row].KeyFunction[col + i] == 3)) {
        w = pointX
        break
      }
      if (props.data[row].KeyFunction[col + i] > 1 || (i == 0 && props.data[row].KeyFunction[col + i] == 3)) {
        pointNum += 1
      }
    }
    w = pointNum > 0 ? pointNum * pointX : w
  }
  return w
}
</script>

<style lang="less" scoped>
svg {
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.03));
  user-select: none;
}

.point_group {
  cursor: pointer;
  transition: var(--transition-base);

  &.active {
    .active_rect {
      filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
    }
  }

  &.default {
    &:hover {
      .default_circle {
        fill: rgba(0, 0, 0, 0.08);
      }
    }
  }
  &.blocked {
    cursor: ew-resize;
    opacity: 0.38;
    pointer-events: all;
  }
}
</style>
